import { insertAuditLog, upsertWorkflowSnapshot } from './api'
import { supabase } from './supabase'

const PROJECT_FIELDS = {
  aircraft_id:        'Aircraft',
  work_order:         'Work Order',
  customer_name:      'Customer',
  arrival_date:       'Arrival Date',
  departure_date:     'Departure Date',
  workscope:          'Workscope',
  departments:        'Departments',
  status:             'Status',
}

const SUBTASK_FIELDS = {
  task_name:  'Task Name',
  department: 'Department',
  start_date: 'Start Date',
  end_date:   'End Date',
  status:     'Status',
}

const CALLOUT_FIELDS = {
  type:     'Type',
  date:     'Start Date',
  end_date: 'End Date',
  text:     'Note',
}

function diff(fieldMap, oldData, newData) {
  return Object.keys(fieldMap).reduce((acc, key) => {
    const from = oldData?.[key] ?? null
    const to   = newData?.[key] ?? null
    if (String(from) !== String(to)) {
      acc.push({ field: fieldMap[key], from: from ?? '—', to: to ?? '—' })
    }
    return acc
  }, [])
}

async function saveWorkflowSnapshot(projectId) {
  try {
    const [{ data: project }, { data: subtasks }, { data: callouts }] = await Promise.all([
      supabase.from('projects').select('*, aircraft(registration, serial_number)').eq('id', projectId).single(),
      supabase.from('subtasks').select('id, task_name, department, start_date, end_date, status, sort_order').eq('project_id', projectId).is('deleted_at', null).order('sort_order', { ascending: true }),
      supabase.from('callouts').select('id, project_id, date, end_date, text, type').eq('project_id', projectId).order('date', { ascending: true }),
    ])
    if (!project) return

    // Capture TSheets actuals so historical snapshots show hours as they were at save time
    const registration = project.aircraft?.registration || ''
    const secondary    = project.secondary_registration || ''
    const serialNumber = project.aircraft?.serial_number || ''
    const startDate    = project.arrival_date || ''
    const tsheetsDeptHours = {}
    if (registration && startDate) {
      const [primary, sec] = await Promise.all([
        supabase.from('tsheets_hours_cache').select('dept_hours').eq('registration', registration).eq('start_date', startDate).maybeSingle(),
        secondary
          ? supabase.from('tsheets_hours_cache').select('dept_hours').eq('registration', secondary).eq('start_date', startDate).maybeSingle()
          : Promise.resolve({ data: null }),
      ])
      const hasHours = src => Object.keys(src?.dept_hours || {}).length > 0
      let serialRows = []
      if (!hasHours(primary.data) && !hasHours(sec.data) && serialNumber) {
        const { data: sd } = await supabase
          .from('tsheets_hours_cache').select('dept_hours')
          .ilike('registration', `%${serialNumber}%`).eq('start_date', startDate)
        serialRows = sd || []
      }
      for (const src of [primary.data, sec.data, ...serialRows]) {
        if (!src) continue
        for (const [k, v] of Object.entries(src.dept_hours || {}))
          tsheetsDeptHours[k] = (tsheetsDeptHours[k] || 0) + v
      }
    }

    await upsertWorkflowSnapshot(projectId, { project, subtasks: subtasks || [], callouts: callouts || [], tsheetsDeptHours })
  } catch (err) {
    console.error('Snapshot error:', err)
  }
}

export async function logChange({
  projectId,
  entityType,   // 'project' | 'subtask' | 'callout'
  entityId,
  entityLabel,
  action,       // 'created' | 'updated' | 'deleted'
  oldData,
  newData,
  changedFields: prebuiltFields = null,  // skip diff when caller supplies fields directly
  userProfile,
  aircraft = [],
}) {
  if (!userProfile) return

  let changedFields = prebuiltFields
  if (changedFields === null && action === 'updated' && oldData && newData) {
    const map = entityType === 'project' ? PROJECT_FIELDS
              : entityType === 'subtask'  ? SUBTASK_FIELDS
              : entityType === 'callout'  ? CALLOUT_FIELDS
              : null
    if (!map) return
    const fields = diff(map, oldData, newData)

    if (entityType === 'project') {
      fields.forEach(f => {
        if (f.field === 'Aircraft') {
          f.from = aircraft.find(a => a.id === f.from)?.registration || f.from
          f.to   = aircraft.find(a => a.id === f.to)?.registration   || f.to
        }
      })
    }

    if (fields.length === 0) return // nothing actually changed, skip log
    changedFields = fields
  }

  try {
    await insertAuditLog({
      project_id:     projectId,
      entity_type:    entityType,
      entity_id:      entityId,
      entity_label:   entityLabel,
      action,
      changed_fields: changedFields,
      user_id:        userProfile.id,
      user_name:      userProfile.display_name || userProfile.email,
      user_email:     userProfile.email,
    })
  } catch (err) {
    console.error('Audit log error:', err)
  }

  // Fire-and-forget: upsert today's snapshot (one per project per day)
  saveWorkflowSnapshot(projectId)
}
